/**
 * useRailFollow
 * Powers the floating collapse/expand handle on the sidebar.
 *
 * The handle is centered by default and follows the cursor's Y position
 * while the cursor is anywhere over the *extended* hover area (the rail
 * rectangle padded on each horizontal side by half the button size, so
 * the area fully covers the protruding halves of the floating button).
 * When the cursor leaves that area, `hoverY` becomes null and the handle
 * eases back to center.
 *
 * We listen to `mousemove` on `window` rather than on the rail itself:
 * the floating button has its own pointer events (so it can be clicked)
 * and would otherwise steal `mouseleave` from the rail the instant the
 * cursor crossed into its bounding box — snapping the handle back to
 * center exactly when the user is about to click it.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface RailBounds {
  railTop: number;
  railRight: number;
  railHeight: number;
}

export interface UseRailFollowOptions {
  /** Diameter of the floating handle (px). The extended hover area is
   *  widened on each horizontal side by `buttonSize / 2`. */
  buttonSize: number;
  /** Padding kept between the handle and the rail top/bottom edges (px). */
  railPadding: number;
  /** Optional callback fired with the current rail rect whenever it
   *  changes. Useful for tests or external observers. */
  onBoundsChange?: (bounds: RailBounds) => void;
}

export interface UseRailFollowResult extends RailBounds {
  /** Cursor Y inside the rail (relative to rail top), or null when the
   *  cursor is outside the extended hover area. */
  hoverY: number | null;
  /** Vertical offset (px) from the rail center for the handle, clamped
   *  so the handle never overflows the rail by less than `railPadding`. */
  delta: number;
  /** Pixel position of the handle's top-left corner in viewport space,
   *  suitable for `position: fixed`. */
  buttonTop: number;
  /** Pixel position of the handle's left edge in viewport space. */
  buttonLeft: number;
  /** True once the hook has measured a real, non-zero rail bounding rect. */
  isPositioned: boolean;
  /** True after the first client-side mount (used to defer portal render). */
  mounted: boolean;
}

export function useRailFollow(
  railRef: React.RefObject<HTMLElement | null>,
  { buttonSize, railPadding, onBoundsChange }: UseRailFollowOptions
): UseRailFollowResult {
  const [hoverY, setHoverY] = useState<number | null>(null);
  const [railTop, setRailTop] = useState(0);
  const [railRight, setRailRight] = useState(0);
  const [railHeight, setRailHeight] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const isPositionedRef = useRef(false);
  const isHoveringRef = useRef(false);

  // `document.body` isn't available during SSR — wait one tick before
  // any portal-friendly state can flip.
  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!mounted) return;

    const readRect = () => {
      const el = railRef.current;
      if (!el) return null;
      return el.getBoundingClientRect();
    };

    const updateBounds = () => {
      const rect = readRect();
      if (!rect) return;
      const next: RailBounds = {
        railTop: rect.top,
        railRight: rect.right,
        railHeight: rect.height,
      };
      setRailTop(next.railTop);
      setRailRight(next.railRight);
      setRailHeight(next.railHeight);
      onBoundsChange?.(next);
      if (!isPositionedRef.current && rect.height > 0 && rect.right > 0) {
        isPositionedRef.current = true;
        setIsPositioned(true);
      }
    };
    updateBounds();

    const ro = new ResizeObserver(updateBounds);
    const el = railRef.current;
    if (el) {
      ro.observe(el);
      // Observe the drawer paper too — its width is what actually
      // changes during collapse/expand (the rail's own size is fixed).
      const paperEl = el.closest('.MuiDrawer-paper');
      if (paperEl) ro.observe(paperEl);
    }

    // Listen on `window` so the floating button (which captures pointer
    // events for its own click handler) can't take hover away from us.
    // See the file header comment for the full story.
    const onMouseMove = (e: MouseEvent) => {
      const rect = readRect();
      if (!rect) return;
      const protrude = buttonSize / 2;
      const inArea =
        e.clientX >= rect.left - protrude &&
        e.clientX <= rect.right + protrude &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (inArea) {
        if (!isHoveringRef.current) isHoveringRef.current = true;
        // Use a microtask-friendly approach: just setState directly.
        setHoverY(e.clientY - rect.top);
      } else if (isHoveringRef.current) {
        isHoveringRef.current = false;
        setHoverY(null);
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('resize', updateBounds);
    // Capture phase so we catch scroll on any ancestor, not just window.
    window.addEventListener('scroll', updateBounds, true);

    return () => {
      ro.disconnect();
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', updateBounds);
      window.removeEventListener('scroll', updateBounds, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, buttonSize]);

  // Clamp the handle so it stays inside the rail with the configured
  // padding. When `hoverY` is null the handle sits dead-center.
  let delta = 0;
  if (hoverY !== null && railHeight > 0) {
    const center = railHeight / 2;
    const halfRange = center - buttonSize / 2 - railPadding;
    delta = Math.max(-halfRange, Math.min(halfRange, hoverY - center));
  }

  const buttonTop = railTop + railHeight / 2 - buttonSize / 2 + delta;
  const buttonLeft = railRight - buttonSize / 2;

  return {
    railTop,
    railRight,
    railHeight,
    hoverY,
    delta,
    buttonTop,
    buttonLeft,
    isPositioned,
    mounted,
  };
}
